import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { journeyService } from '../../services/journey.service'

// Tipos definidos localmente para evitar problemas de importação
export interface JourneyResponse {
  journeyId: number
  userId: number
  company: string
  routeId: number
  totalAmount: number
  closingTime: string | null
  status: string
  creationDate: string
  updateDate: string
  companyName?: string
  userName?: string
}

export interface JourneyListState {
  journeys: JourneyResponse[]
  loading: boolean
  error: string | null
  pagination: {
    currentPage: number
    pageSize: number
    totalItems: number
    totalPages: number
    hasPreviousPage: boolean
    hasNextPage: boolean
  } | null
}

// Estado inicial
const initialState: JourneyListState = {
  journeys: [],
  loading: false,
  error: null,
  pagination: null,
}

// Async thunk simplificado
export const fetchPreviousClosings = createAsyncThunk(
  'journey/fetchPreviousClosings',
  async () => {
    const response = await journeyService.getPreviousClosings()
    return response
  },
)

// Async thunk para busca paginada com filtros
export const fetchPreviousClosingsPaged = createAsyncThunk(
  'journey/fetchPreviousClosingsPaged',
  async (params: {
    page?: number
    pageSize?: number
    company?: string
    status?: number | undefined
    startDate?: string
    endDate?: string
  }) => {
    try {
      const response = await journeyService.getPreviousClosingsPaged(
        params.page,
        params.pageSize,
        params.company,
        params.status,
        params.startDate,
        params.endDate,
      )
      return response
    } catch (error) {
      throw error
    }
  },
)

// Slice do Redux
const journeySlice = createSlice({
  name: 'journey',
  initialState,
  reducers: {
    // Limpar erros
    clearError: state => {
      state.error = null
    },

    // Limpar jornadas
    clearJourneys: state => {
      state.journeys = []
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Previous Closings
      .addCase(fetchPreviousClosings.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPreviousClosings.fulfilled, (state, action) => {
        state.loading = false
        state.journeys = action.payload
        state.error = null
      })
      .addCase(fetchPreviousClosings.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.error.message || 'Error al cargar datos. Inténtalo nuevamente.'
      })
      // Fetch Previous Closings Paged
      .addCase(fetchPreviousClosingsPaged.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPreviousClosingsPaged.fulfilled, (state, action) => {
        state.loading = false
        state.journeys = action.payload.data || []
        state.pagination = action.payload.pagination || {
          currentPage: 1,
          pageSize: 10,
          totalItems: 0,
          totalPages: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        }
        state.error = null
      })
      .addCase(fetchPreviousClosingsPaged.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.error.message || 'Error al cargar datos. Inténtalo nuevamente.'
      })
  },
})

export const { clearError, clearJourneys } = journeySlice.actions
export default journeySlice.reducer
